import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { SessionOuverte, UtilisateurCourant } from '@afs/shared'
import { definirJetonAcces, restaurerSession } from '../../shared/api/client.js'
import { estErreurApi } from '../../shared/api/erreurs.js'
import { identifierPourSuiviErreurs } from '../../shared/suivi-erreurs.js'
import { oublierCle } from '../hors-ligne/synchro.js'
import { oublierSynchro } from '../hors-ligne/useSynchroHorsLigne.js'
import { clesAuth, utilisateurCourant } from './api.js'

/**
 * Session courante.
 *
 * L'utilisateur n'est PAS recopié dans un `useState` : il vit dans le cache
 * TanStack Query, source unique. Deux copies d'une même donnée divergent
 * toujours, et silencieusement (skill `frontend-react-afs` § Structure).
 *
 * Au démarrage, le jeton d'accès n'existe pas — il vit en mémoire et la page
 * vient d'être chargée. `restaurerSession()` tente de le reconstruire à
 * partir du cookie `httpOnly` de rafraîchissement : sans ça, un simple F5
 * renverrait sur l'écran de connexion.
 */
export function useSession(): {
  utilisateur: UtilisateurCourant | undefined
  chargement: boolean
  erreur: unknown
} {
  const requeteSession = useQuery({
    queryKey: clesAuth.session(),
    queryFn: async () => {
      const restauree = await restaurerSession()
      if (!restauree) return null
      const utilisateur = await utilisateurCourant()
      identifierPourSuiviErreurs(utilisateur)
      return utilisateur
    },
    // L'identité ne change pas en cours de session ; une éventuelle
    // modification de rôle est relue au prochain montage ou après mutation.
    staleTime: 5 * 60_000,
    // Un 401 est une réponse, pas une panne : le réessayer est inutile.
    retry: (nombre, erreur) => !estErreurApi(erreur) && nombre < 1,
  })

  return {
    utilisateur: requeteSession.data ?? undefined,
    chargement: requeteSession.isPending,
    erreur: requeteSession.error,
  }
}

/**
 * Installe une session fraîchement ouverte. Utilisé par la connexion simple
 * ET par la vérification du second facteur, qui aboutissent au même état.
 */
export function useOuvrirSession(): (session: SessionOuverte) => void {
  const client = useQueryClient()

  return (session: SessionOuverte) => {
    definirJetonAcces(session.jetonAcces)
    // On n'écrit PAS `session.utilisateur` directement dans le cache : la
    // connexion ne rend ni `totpActive` ni `derniereConnexion`, et compléter
    // avec des valeurs inventées ferait mentir le type. Un aller-retour vers
    // `/auth/moi` coûte moins cher qu'une donnée fausse — et c'est lui qui
    // décide ensuite si l'enrôlement du second facteur s'impose.
    //
    // `resetQueries`, pas `invalidateQueries` : l'invalidation ne relance que
    // les requêtes observées, et personne n'observe la session depuis l'écran
    // de connexion. Le `null` mis en cache par la garde quand l'utilisateur
    // est arrivé déconnecté restait donc en place, `isPending` était faux, et
    // la première connexion renvoyait aussitôt sur `/connexion`. Remettre la
    // requête à son état initial force la garde à attendre la vraie réponse.
    void client.resetQueries({ queryKey: clesAuth.session(), exact: true })
  }
}

/** Efface toute trace locale de la session. Le serveur, lui, révoque. */
export function useFermerSession(): () => void {
  const client = useQueryClient()

  return () => {
    // La clé du coffre ne survit pas à la session ; le coffre, chiffré, reste
    // pour la coupure. Ici et pas dans la coque : un mot de passe changé ferme
    // aussi la session, et le coffre doit se revérifier sous le nouveau (S396).
    oublierCle()
    oublierSynchro()
    definirJetonAcces(null)
    identifierPourSuiviErreurs(null)
    client.setQueryData(clesAuth.session(), null)
    // Tout le cache est vidé : une donnée patient ne doit pas rester lisible
    // après une déconnexion, même le temps d'un rendu.
    client.clear()
  }
}
